'use client';

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer';
import {
  documentToReactComponents,
  type Options,
} from '@contentful/rich-text-react-renderer';
import type {Block, Document, Inline} from '@contentful/rich-text-types';
import {BLOCKS} from '@contentful/rich-text-types';
import type {ReactNode} from 'react';
import styles from './div.module.scss';
import {type AcceptableDocument, isDocument} from './document-like';

export interface RichProps {
  children?: AcceptableDocument;
  options?: Options;
}

type LinkAttributes = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

const isParagraphEmpty = (node: Block | Inline): boolean => {
  if (!node) return true;
  return documentToPlainTextString(node).trim().length === 0;
};

const EXTERNAL_LINK_ATTRIBUTES = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

const isExternal = (uri: string) =>
  uri.startsWith('http') || uri.startsWith('//');

const renderText = (text: string) =>
  text
    .split('\n')
    .reduce((acc: ReactNode[], textSegment: string, index: number) => {
      const breakKey = `break-${acc.length}-${textSegment.length}`;
      return [...acc, index > 0 && <br key={breakKey} />, textSegment];
    }, []);

export const Rich = ({children, options = {}}: RichProps) => {
  if (!isDocument(children)) {
    return null;
  }
  const opts: Options = {
    renderNode: {
      hyperlink: (node, children) => {
        const uri = node.data.uri;
        const additionaAttributes: LinkAttributes = isExternal(uri)
          ? EXTERNAL_LINK_ATTRIBUTES
          : {};

        return (
          <a
            href={uri}
            {...additionaAttributes}
            className={styles.link}
          >
            {children}
          </a>
        );
      },
      ...(options.renderNode || {}),
    },
    renderText: options.renderText || renderText,
  };
  return documentToReactComponents(children, opts);
};

export const RichNoP = ({children}: RichProps) => (
  <Rich
    options={{
      renderNode: {
        [BLOCKS.PARAGRAPH]: (
          node: Block | Inline,
          children: React.ReactNode,
        ) => {
          const isEmpty = isParagraphEmpty(node);
          if (isEmpty) {
            // Render a placeholder to ensure line-height for empty paragraphs.
            return <div className={styles.isEmpty} />;
          }
          // Preserve original "NoP" behavior for non-empty paragraphs.
          return <>{children}</>;
        },
      },
    }}
  >
    {children}
  </Rich>
);

export const getDocumentToPlainTextString = (
  document: Document | unknown,
): string => documentToPlainTextString(document as Document) ?? '';
